import {useMutation} from "@apollo/client";
import {useContext} from "react";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import {FiExternalLink, FiX} from "react-icons/fi";
import {Link} from "react-router";

import {Card} from "@/components/card/Card";
import {NewCheckVaultItemsContext} from "@/components/context/NewCheckVaultItemsContextInterface.tsx";
import {Icon} from "@/components/icon/Icon";
import {EllipsisText} from "@/components/typography/EllipsisText.tsx";
import {
  GQL_SERVICE_VARIABLES_DELETE,
  GQL_SERVICE_VARIABLES_GROUP_BY_CREDENTIAL_ID_QUERY,
} from "@/lib/queries/serviceVariables.ts";
import {Credentials, CredentialTypeDisplayDetails, CredentialTypeLabel} from "@/lib/types/credentials.ts";
import {CredentialProperties, ServiceVariable, ServiceVariableDeleteResponse} from "@/lib/types/serviceVariables.ts";
import {VaultItemServiceVariableInput} from "@/pages/checks/checkVaultServiceVariables/VaultItemServiceVariableInput.tsx";

import styles from "./VaultItem.module.scss";

interface ServiceCredentialVariablesProps {
  credential: Credentials;
  serviceVariables: ServiceVariable[];
}

export const VaultItem = ({credential, serviceVariables}: ServiceCredentialVariablesProps) => {
  const {checkId: contextCheckId, deleteVaultItem} = useContext(NewCheckVaultItemsContext);
  const [deleteServiceVariables] = useMutation<ServiceVariableDeleteResponse>(GQL_SERVICE_VARIABLES_DELETE, {
    refetchQueries: [
      {
        query: GQL_SERVICE_VARIABLES_GROUP_BY_CREDENTIAL_ID_QUERY,
        variables: {
          service_id: contextCheckId,
        },
      },
    ],
  });

  const deleteHandler = async () => {
    if (!contextCheckId) {
      deleteVaultItem(credential.id);
      return;
    }
    const serviceVariableIds = serviceVariables.map((serviceVariable) => serviceVariable.id);
    await deleteServiceVariables({
      variables: {
        ids: serviceVariableIds,
      },
    });
  };

  const sortedServiceVariables = serviceVariables
    .slice()
    .sort(
      (a, b) =>
        CredentialProperties[credential.credential_type].indexOf(a.property_name) -
        CredentialProperties[credential.credential_type].indexOf(b.property_name)
    );

  // cannot use Icon.tsx in inline component
  const CredentialIcon = CredentialTypeDisplayDetails[credential.credential_type].icon;

  return (
    <Card variant="white" className={styles.vaultItem}>
      <div className={"d-flex justify-content-center pe-1"} style={{gridArea: "icon"}}>
        <CredentialIcon />
      </div>
      <div className={"d-flex gap-1 flex-column overflow-hidden mb-2"} style={{gridArea: "title"}}>
        <Link to={"/vault?credentialId=" + credential.id} target={"_blank"}>
          <div className={"d-flex pulse-color-black align-items-center"}>
            <EllipsisText className={"pulseTextBtn pulse-color-gray-800"}>{credential.display_name}</EllipsisText>
            <OverlayTrigger
              placement="top"
              delay={{show: 500, hide: 200}}
              overlay={(props) => <Tooltip {...props}>View in the Vault</Tooltip>}
            >
              <div className={"d-flex align-self-center ms-2 pulse-color-gray-500"}>
                <Icon icon={FiExternalLink} display="inline" />
              </div>
            </OverlayTrigger>
          </div>
        </Link>
        <div className={"pulseTextBodySmall pulse-color-gray-500"}>
          {CredentialTypeLabel[credential.credential_type]}
        </div>
      </div>
      <div className={"d-flex flex-column align-items-center mb-2"} style={{gridArea: "delete"}}>
        <button type="button" onClick={deleteHandler} className={"btn border-0 text-decoration-none"}>
          <Icon icon={FiX} className={"pulse-color-gray-500"} />
        </button>
      </div>
      <div className={"d-flex justify-content-center pe-1"} style={{gridArea: "vertical-line"}}>
        <div className={"vr pulse-bg-gray-400"}></div>
      </div>
      <div className={"d-flex flex-column pt-2"} style={{gridArea: "service-variables"}}>
        {sortedServiceVariables.map((serviceVariable) => {
          return (
            <VaultItemServiceVariableInput serviceVariable={serviceVariable} key={serviceVariable.property_name} />
          );
        })}
      </div>
    </Card>
  );
};
